import React, { useState, useEffect, useContext, useRef } from "react";
import {
  Container,
  Stack,
  AppBar,
  Toolbar,
  Typography,
  Dialog,
  IconButton,
  TableContainer,
  Table,
  TableCell,
  TableRow,
  TableHead,
  TableBody,
  Divider,
  Box,
  TableSortLabel,
} from "@mui/material";
import { isEmpty } from "lodash";
import { useParams } from "react-router-dom";
import ReactToPrint from "react-to-print";
import PrintRoundedIcon from "@mui/icons-material/PrintRounded";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCloudArrowDown } from "@fortawesome/free-solid-svg-icons";
import moment from "moment";
import * as XLSX from "xlsx/xlsx.mjs";

import { DataContext } from "../../../context/DataContext";
import { getRol, getToken, getUser } from "../../../api/Account";
import { getComportamientoPagoAsync } from "../../../api/Report";
import { simpleMessage } from "../../../helpers/Helpers";
import {
  getGestoresAsync,
  getGestoresBySucursalAccessAsync,
  getSucursalByUserAsync,
} from "../../../api/Users";
import NoData from "../../../components/NoData";
import { PrintReport } from "../../../components/PrintReport";
import { useOrdenTabla } from "../../../helpers/OrdenTabla";

const moneda = (valor) =>
  new Intl.NumberFormat("es-NI", { style: "currency", currency: "NIO" }).format(
    valor
  );

/**
 * Qué significa cada letra. Los cortes están en el backend; acá solo se les pone nombre y
 * color, para que la letra no haya que ir a buscarla a ninguna parte.
 */
const CALIFICACIONES = {
  A: { etiqueta: "Paga en fecha", color: "#00a152" },
  B: { etiqueta: "Se atrasa poco", color: "#1769aa" },
  C: { etiqueta: "Se atrasa siempre", color: "#ed6c02" },
  D: { etiqueta: "Riesgo", color: "#d32f2f" },
};

const colorDe = (calificacion) => CALIFICACIONES[calificacion]?.color ?? "inherit";

/**
 * Las columnas, en orden. `campo` es la propiedad por la que se ordena y `texto` marca
 * las que se comparan como palabras y no como números.
 */
const COLUMNAS = [
  { campo: "fullName", titulo: "Cliente", align: "left", texto: true, encabezado: true },
  { campo: "phone", titulo: "Teléfono", align: "center", texto: true },
  { campo: "gestor", titulo: "Gestor", align: "left", texto: true, encabezado: true },
  { campo: "ciclos", titulo: "Ciclos", align: "center" },
  { campo: "saldoActual", titulo: "Saldo Actual", align: "center", moneda: true },
  { campo: "cuotasVencidas", titulo: "Cuotas", align: "center" },
  { campo: "cuotasPuntuales", titulo: "Puntuales", align: "center" },
  { campo: "cuotasTarde", titulo: "Tarde", align: "center" },
  { campo: "cuotasSinPagar", titulo: "Sin Pagar", align: "center" },
  { campo: "porcentajePuntual", titulo: "% Puntual", align: "center", sufijo: "%" },
  { campo: "promedioDiasMora", titulo: "Prom.Mora", align: "center", sufijo: " d" },
  { campo: "peorAtraso", titulo: "Peor Atraso", align: "center", sufijo: " d" },
  { campo: "tendencia", titulo: "Tendencia", align: "center", texto: true },
  { campo: "calificacion", titulo: "Calif.", align: "center", texto: true },
];

const ComportamientoPago = () => {
  const { selectedSucursal, selectedGestor, soloActivos } = useParams();
  const [data, setData] = useState([]);
  const [criterios, setCriterios] = useState(null);
  const [sucursalName, setSucursalName] = useState("");
  const [gestorName, setGestorName] = useState("");

  // Arranca por promedio de mora de mayor a menor, que es como lo manda el servidor: los
  // peores primero. Desde ahí se reordena por cualquier columna.
  const { orden, cambiarOrden, ordenar } = useOrdenTabla(
    "promedioDiasMora",
    COLUMNAS.filter((c) => c.texto).map((c) => c.campo)
  );

  const compRef = useRef();
  const { setIsLoading, setIsUnautorized, title, slogan, icon } =
    useContext(DataContext);

  const token = getToken();
  const user = getUser();
  const rol = getRol();

  useEffect(() => {
    (async () => {
      await getSucursals();
      await getGestores();

      setIsLoading(true);
      const result = await getComportamientoPagoAsync(token, {
        sucursalId: selectedSucursal === "t" ? 0 : Number(selectedSucursal),
        gestorId: selectedGestor === "t" ? 0 : Number(selectedGestor),
        soloActivos: soloActivos === "true",
      });
      setIsLoading(false);

      if (!result.statusResponse) {
        if (result.error === "Unauthorized") {
          setIsUnautorized(true);
          return;
        }
        simpleMessage(result.error, "error");
        return;
      }

      setData(result.data.result.clientes);
      setCriterios(result.data.result.criterios);
    })();
  }, []);

  const getSucursals = async () => {
    const result = await getSucursalByUserAsync(token);
    if (!result.statusResponse) {
      return;
    }
    const encontrada = result.data.result.filter(
      (s) => s.id.toString() === selectedSucursal
    );
    setSucursalName(isEmpty(encontrada) ? "" : encontrada[0].description);
  };

  const getGestores = async () => {
    const result =
      selectedSucursal === "t"
        ? await getGestoresBySucursalAccessAsync(token)
        : await getGestoresAsync(token, selectedSucursal);
    if (!result.statusResponse) {
      return;
    }
    const encontrado = result.data.result.filter(
      (g) => g.id.toString() === selectedGestor
    );
    setGestorName(isEmpty(encontrado) ? "" : encontrado[0].fullName);
  };

  const pageStyle = `
  @media print {
    html, body {
      height: initial !important;
      overflow: initial !important;
      -webkit-print-color-adjust: exact;
    }
  }
  @media all {
    .pagebreak {
      display: none;
    }
  }
  @media print {
    .pagebreak {
      page-break-before: always;
    }
  }
  @page { size: landscape;  margin: 4mm; }
`;

  const ordenados = ordenar(data);

  const contar = (calificacion) =>
    data.filter((c) => c.calificacion === calificacion).length;

  const saldoDe = (calificacion) =>
    data
      .filter((c) => c.calificacion === calificacion)
      .reduce((t, c) => t + c.saldoActual, 0);

  const totalSaldo = data.reduce((t, c) => t + c.saldoActual, 0);

  const exportExcel = () => {
    let exp = [];

    // Sale en el orden que se está viendo en pantalla, no en el que vino del servidor: si
    // alguien ordenó por saldo y exporta, espera el archivo ordenado por saldo.
    ordenados.forEach((item) => {
      exp.push({
        cliente: item.fullName,
        telefono: item.phone,
        sucursal: item.sucursal,
        gestor: item.gestor,
        ciclos: item.ciclos,
        creditoActivo: item.tieneCreditoActivo ? "SI" : "NO",
        saldoActual: item.saldoActual,
        cuotasVencidas: item.cuotasVencidas,
        cuotasPuntuales: item.cuotasPuntuales,
        cuotasTarde: item.cuotasTarde,
        cuotasSinPagar: item.cuotasSinPagar,
        porcentajePuntual: item.porcentajePuntual,
        promedioDiasMora: item.promedioDiasMora,
        peorAtraso: item.peorAtraso,
        tendencia: item.tendencia,
        calificacion: item.calificacion,
      });
    });

    var bk = XLSX.utils.book_new(),
      bs = XLSX.utils.json_to_sheet(exp);

    XLSX.utils.book_append_sheet(bk, bs, "comportamientoPago");
    XLSX.writeFile(bk, "ComportamientoPago.xlsx");
  };

  const t = (compacto, chico, grande) => (compacto ? chico : grande);

  const parametros = (compacto) => (
    <Stack direction={"row"} justifyContent="space-between">
      <Stack>
        <Typography
          sx={{ color: "text.secondary", textAlign: "left" }}
          fontSize={t(compacto, 11, 13)}
        >
          Parametros:
        </Typography>
        <Typography fontSize={t(compacto, 11, 14)} textAlign="left">
          {`Sucursal: ${selectedSucursal === "t" ? "Todas" : sucursalName}`}
        </Typography>
        <Typography fontSize={t(compacto, 11, 14)} textAlign="left">
          {`Gestor Seleccionado: ${selectedGestor === "t" ? "Todos" : gestorName}`}
        </Typography>
        <Typography fontSize={t(compacto, 11, 14)} textAlign="left">
          {`Clientes: ${
            soloActivos === "true" ? "Solo con crédito activo" : "Todos, incluso sin crédito"
          }`}
        </Typography>
      </Stack>

      <Stack>
        <Typography
          sx={{ color: "text.secondary", textAlign: "left" }}
          fontSize={t(compacto, 11, 13)}
        >
          Usuario:
        </Typography>
        <Typography fontSize={t(compacto, 11, 14)} textAlign="left">
          {user}
        </Typography>
        <Typography fontSize={t(compacto, 11, 14)} textAlign="left">
          {rol}
        </Typography>
        <Typography fontSize={t(compacto, 11, 14)} textAlign="left">
          {`Fecha y Hora: ${moment().format("DD/MM/YYYY hh:mm A")}`}
        </Typography>
      </Stack>
    </Stack>
  );

  const totales = (compacto) => {
    const tamano = t(compacto, 11, 14);

    const Total = ({ titulo, valor, color }) => (
      <Stack textAlign="center">
        <Typography
          style={{ fontWeight: "bold", color: color ?? "#03a9f4", fontSize: tamano }}
        >
          {titulo}
        </Typography>
        <Typography style={{ fontSize: tamano }}>{valor}</Typography>
      </Stack>
    );

    return (
      <>
        <Stack direction="row" flex="row" justifyContent="space-around">
          <Total titulo="Clientes" valor={data.length} />
          {["A", "B", "C", "D"].map((c) => (
            <Total
              key={c}
              titulo={`${c} · ${CALIFICACIONES[c].etiqueta}`}
              valor={contar(c)}
              color={CALIFICACIONES[c].color}
            />
          ))}
        </Stack>

        <Divider sx={{ marginY: "10px" }} />

        <Stack direction="row" flex="row" justifyContent="space-around">
          {/* El saldo por calificación es lo que convierte el reporte en una decisión:
          dice cuánta plata está parada en clientes que no pagan bien. */}
          {["A", "B", "C", "D"].map((c) => (
            <Total
              key={c}
              titulo={`Saldo ${c}`}
              valor={moneda(saldoDe(c))}
              color={CALIFICACIONES[c].color}
            />
          ))}
          <Total titulo="Total Saldo" valor={moneda(totalSaldo)} />
        </Stack>
      </>
    );
  };

  const valorDe = (row, columna) => {
    const valor = row[columna.campo];
    if (columna.moneda) {
      return moneda(valor);
    }
    if (columna.sufijo) {
      return `${valor}${columna.sufijo}`;
    }
    return valor;
  };

  const tabla = (compacto) => {
    const th = { fontSize: t(compacto, 9, 14) };
    const td = { fontSize: t(compacto, 8, 13) };

    return (
      <TableContainer>
        <Table aria-label="simple table" size="small">
          <TableHead>
            <TableRow>
              {COLUMNAS.map((columna) => (
                <TableCell
                  key={columna.campo}
                  align={columna.align}
                  className="text-primary fw-bold"
                  style={th}
                  sortDirection={
                    orden.campo === columna.campo ? (orden.asc ? "asc" : "desc") : false
                  }
                >
                  {/* En papel el encabezado va plano: la flecha de ordenar no significa
                  nada impresa, y el orden ya quedó fijado al mandar a imprimir. */}
                  {compacto ? (
                    columna.titulo
                  ) : (
                    <TableSortLabel
                      active={orden.campo === columna.campo}
                      direction={orden.asc ? "asc" : "desc"}
                      onClick={() => cambiarOrden(columna.campo)}
                    >
                      {columna.titulo}
                    </TableSortLabel>
                  )}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {ordenados.map((row) => (
              <TableRow key={row.clientId}>
                {COLUMNAS.map((columna) => (
                  <TableCell
                    key={columna.campo}
                    align={columna.align}
                    component={columna.encabezado ? "th" : undefined}
                    scope="row"
                    style={
                      columna.campo === "calificacion"
                        ? { ...td, fontWeight: "bold", color: colorDe(row.calificacion) }
                        : td
                    }
                  >
                    {valorDe(row, columna)}
                  </TableCell>
                ))}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    );
  };

  /**
   * En qué se basan la calificación y la tendencia. Los umbrales salen del propio reporte
   * y no están escritos acá a mano: si algún día se ajustan en el cálculo, la leyenda se
   * ajusta con ellos en vez de quedar explicando un criterio viejo.
   */
  const leyenda = (compacto) => {
    if (criterios === null) {
      return null;
    }

    const chico = t(compacto, 8, 12);
    const titulo = t(compacto, 9, 13);

    const significado = {
      A: `atraso promedio de hasta ${criterios.diasA} día`,
      B: `atraso promedio de hasta ${criterios.diasB} días`,
      C: `atraso promedio de hasta ${criterios.diasC} días`,
      D: `atraso promedio mayor a ${criterios.diasC} días, o con cuotas sin pagar de más de ${criterios.diasRiesgo} días`,
    };

    return (
      <>
        <Divider sx={{ marginY: "10px" }} />

        <Typography
          fontSize={titulo}
          fontWeight="bold"
          textAlign="left"
          sx={{ color: "text.secondary" }}
        >
          Cómo se lee este reporte
        </Typography>

        <Stack marginTop={0.5}>
          {["A", "B", "C", "D"].map((c) => (
            <Typography key={c} fontSize={chico} textAlign="left">
              <span style={{ color: CALIFICACIONES[c].color, fontWeight: "bold" }}>
                {`Cliente ${c} · ${CALIFICACIONES[c].etiqueta}: `}
              </span>
              {significado[c]}.
            </Typography>
          ))}

          <Typography fontSize={chico} textAlign="left" marginTop={0.5}>
            <span style={{ fontWeight: "bold" }}>Tendencia: </span>
            {`compara el atraso promedio de sus últimas ${criterios.cuotasRecientes} cuotas vencidas contra el de todas las anteriores. Dice "Mejorando" si se atrasa al menos ${criterios.cambioSignificativoDias} días menos que antes, "Empeorando" si se atrasa al menos ${criterios.cambioSignificativoDias} días más, y "Estable" si la diferencia es menor. Queda en blanco con menos de ${criterios.cuotasMinimasTendencia} cuotas vencidas, porque con pocas cualquier diferencia es casualidad.`}
          </Typography>

          <Typography fontSize={chico} textAlign="left" marginTop={0.5}>
            <span style={{ fontWeight: "bold" }}>Cuotas: </span>
            solo las que ya llegaron a su fecha de vencimiento; las que aún no vencen no
            dicen nada de cómo paga. <span style={{ fontWeight: "bold" }}>Prom.Mora</span>{" "}
            son los días de atraso promedio sobre esas cuotas: en una cuota ya pagada, los
            días con que se pagó; en una sin pagar, los que lleva acumulados a hoy.
          </Typography>
        </Stack>
      </>
    );
  };

  return (
    <div>
      <Dialog fullScreen open={true}>
        <AppBar sx={{ position: "relative" }}>
          <Toolbar>
            <img loading="lazy" src={icon} alt="logo" style={{ height: 40 }} />
            <Typography
              sx={{ ml: 2, flex: 1, textAlign: "center" }}
              variant="h5"
              component="div"
            >
              {`${title} `}
            </Typography>
            <img loading="lazy" src={icon} alt="logo" style={{ height: 40 }} />
          </Toolbar>
        </AppBar>

        <Stack display="flex" justifyContent="center">
          <Stack
            direction={"row"}
            justifyContent="space-between"
            paddingLeft={5}
            paddingRight={5}
            marginTop={1}
            marginBottom={1}
          >
            <IconButton
              onClick={() => exportExcel()}
              sx={{ color: "primary.main" }}
            >
              <FontAwesomeIcon
                icon={faCloudArrowDown}
                style={{ fontSize: 22, width: 22 }}
              />
            </IconButton>

            <Stack>
              <Typography
                sx={{
                  color: "text.secondary",
                  textAlign: "center",
                  fontWeight: "bold",
                }}
              >
                {slogan}
              </Typography>
              <Typography
                sx={{
                  color: "text.secondary",
                  textAlign: "center",
                  fontWeight: "bold",
                }}
                variant="h6"
                component="div"
              >
                Reporte de Comportamiento de Pago
              </Typography>
            </Stack>

            <ReactToPrint
              trigger={() => {
                return (
                  <IconButton sx={{ color: "info.main" }}>
                    <PrintRoundedIcon style={{ fontSize: 22, width: 22 }} />
                  </IconButton>
                );
              }}
              content={() => compRef.current}
              pageStyle={pageStyle}
            />
          </Stack>

          <Box sx={{ paddingX: "22px", textAlign: "center" }}>
            {parametros(false)}
          </Box>
        </Stack>

        <Box sx={{ paddingX: "22px", textAlign: "center" }}>
          <Divider sx={{ marginY: "14px" }} />

          {isEmpty(data) ? (
            <NoData />
          ) : (
            <>
              {totales(false)}
              <Divider sx={{ marginY: "14px" }} />
              {tabla(false)}
              {leyenda(false)}
            </>
          )}
        </Box>
      </Dialog>

      <div style={{ display: "none" }}>
        <PrintReport ref={compRef} titulo={"Reporte de Comportamiento de Pago"}>
          <Container maxWidth={false} sx={{ textAlign: "center" }}>
            {parametros(true)}
            <Divider sx={{ marginY: "10px" }} />

            {totales(true)}
            <Divider sx={{ marginY: "10px" }} />

            {tabla(true)}
            {leyenda(true)}
          </Container>
        </PrintReport>
      </div>
    </div>
  );
};

export default ComportamientoPago;
